import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

interface CustomerBalance {
  customer_id: number;
  company_name: string;
  contact_name: string;
  email: string;
  total_invoiced: number;
  total_paid: number;
  balance_due: number;
  credit_balance: number;
  last_payment_date: string | null;
  last_invoice_date: string | null;
}

const CustomerBalanceList: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [customerBalances, setCustomerBalances] = useState<CustomerBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [balanceFilter, setBalanceFilter] = useState('');

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchCustomerBalances();
    }
  }, [isAuthenticated, authLoading, searchTerm, balanceFilter]);

  const fetchCustomerBalances = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (searchTerm) params.append('search', searchTerm);
      if (balanceFilter) params.append('filter', balanceFilter);
      
      const response = await api.get(`/customers/balances?${params.toString()}`);
      if (response.data.success) {
        setCustomerBalances(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching customer balances:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBalanceStatus = (balance: CustomerBalance): { status: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' } => {
    if (balance.credit_balance > 0) {
      return { status: 'Credit', color: 'info' };
    } else if (balance.balance_due > 0) {
      return { status: 'Outstanding', color: 'warning' };
    } else {
      return { status: 'Paid Up', color: 'success' };
    }
  };

  const getTotalStats = () => {
    return customerBalances.reduce(
      (acc, balance) => ({
        totalInvoiced: acc.totalInvoiced + Number(balance.total_invoiced || 0),
        totalPaid: acc.totalPaid + Number(balance.total_paid || 0),
        totalOutstanding: acc.totalOutstanding + Number(balance.balance_due || 0),
        totalCredit: acc.totalCredit + Number(balance.credit_balance || 0),
      }),
      { totalInvoiced: 0, totalPaid: 0, totalOutstanding: 0, totalCredit: 0 }
    );
  };

  const stats = getTotalStats();

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Please log in to view customer balances.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Customer Balances
        </Typography>
      </Box>

      {/* Summary Cards - hidden for users without payments.read */}
      {hasPermission('payments.read') && (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3,
          mb: 3
        }}
      >
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <AccountBalanceIcon color="primary" />
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(stats.totalInvoiced)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Invoiced
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <PaymentIcon color="success" />
              <Box>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {formatCurrency(stats.totalPaid)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Paid
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <TrendingUpIcon color="warning" />
              <Box>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  {formatCurrency(stats.totalOutstanding)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Outstanding
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <TrendingDownIcon color="info" />
              <Box>
                <Typography variant="h6" fontWeight="bold" color="info.main">
                  {formatCurrency(stats.totalCredit)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Credit
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 3, 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
            <TextField
              fullWidth
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ borderRadius: 2 }}
            />
          </Box>
          <Box sx={{ flex: '0 0 200px', minWidth: '200px' }}>
            <FormControl fullWidth>
              <InputLabel>Balance Status</InputLabel>
              <Select
                value={balanceFilter}
                label="Balance Status"
                onChange={(e) => setBalanceFilter(e.target.value)}
              >
                <MenuItem value="">All Customers</MenuItem>
                <MenuItem value="outstanding">Outstanding Balance</MenuItem>
                <MenuItem value="credit">Credit Balance</MenuItem>
                <MenuItem value="zero">Zero Balance</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '0 0 120px', minWidth: '120px' }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={fetchCustomerBalances}
              sx={{ height: '56px' }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Customer Balances Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Contact</TableCell>
                  {hasPermission('payments.read') && (
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total Invoiced</TableCell>
                  )}
                  {hasPermission('payments.read') && (
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total Paid</TableCell>
                  )}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Balance Due</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Credit Balance</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Payment</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customerBalances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No customer balances found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customerBalances.map((balance) => {
                    const balanceStatus = getBalanceStatus(balance);
                    return (
                      <TableRow
                        key={balance.customer_id}
                        hover
                        onClick={() => navigate(`/customers/${balance.customer_id}/balance`)}
                        sx={{
                          '&:hover': { backgroundColor: 'action.hover' },
                          cursor: 'pointer'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {balance.company_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {balance.contact_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {balance.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        {hasPermission('payments.read') && (
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(balance.total_invoiced)}
                          </Typography>
                        </TableCell>
                        )}
                        {hasPermission('payments.read') && (
                        <TableCell>
                          <Typography variant="body2" color="success.main" fontWeight="medium">
                            {formatCurrency(balance.total_paid)}
                          </Typography>
                        </TableCell>
                        )}
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color={balance.balance_due > 0 ? "warning.main" : "text.secondary"}
                            fontWeight="medium"
                          >
                            {formatCurrency(balance.balance_due)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color={balance.credit_balance > 0 ? "info.main" : "text.secondary"}
                            fontWeight="medium"
                          >
                            {formatCurrency(balance.credit_balance)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={balanceStatus.status}
                            color={balanceStatus.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {balance.last_payment_date 
                              ? new Date(balance.last_payment_date).toLocaleDateString()
                              : 'No payments'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/customers/${balance.customer_id}/balance`)}
                              sx={{ color: 'primary.main' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            {hasPermission('payments.create') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/payments/new?customerId=${balance.customer_id}`)}
                              sx={{ color: 'success.main' }}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerBalanceList;